import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { WorkflowService, WorkflowUriBuilderParams } from '@app/core/services/workflow/workflow.service';
import {
    CaseWorkflowSettingsTypes,
    Workflow,
    WorkflowTargets
} from '@app/shared/models/workflow';
import { BaseWithIdList } from '@app/shared/models/list';
import { CreateNameComponent } from '@app/shared/components/create-name/create-name.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionListBtnConfig, ActionListOption } from '@app/shared/components/action-list/action-list.component';
import { OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';


@Component({
    selector: 'workflow-target',
    templateUrl: './workflow-target.component.html',
    styleUrls: ['./workflow-target.component.scss']
})
export class WorkflowTargetComponent extends BaseWithBundleComponent
                                     implements OnInit, OnDestroy {

    @ViewChild('createNameComponent')
    public createNameComponent: CreateNameComponent;

    @ViewChild("tabGroup", { static: false })
    public tabGroup: MatTabGroup;

    @Input()
    public target: WorkflowTargets;

    public workflowList: BaseWithIdList<Workflow>;
    public selected: Workflow;
    public actionListOptions: ActionListBtnConfig<Workflow>[];

    private copyWorkflow: Workflow;

    public tabs: { code: string, label: string, hide: boolean, index?: number }[];
    public selectedTabContentCode: string;

    get CaseWorkflowSettingsTypes() {
        return CaseWorkflowSettingsTypes;
    }

    public constructor(protected componentServiceBundle: ComponentServiceBundle,
                       protected workflowService: WorkflowService) {
        super(componentServiceBundle);
    }

    public ngOnInit() {
        super.ngOnInit();

        this.initActionListOptions();

        this.workflowService.query(this.generateUriBuilderParams())
                            .subscribe((workflowList) => {
            this.workflowList = workflowList;

            if (this.workflowList.items.length) {
                this.select(this.workflowList.items[0]);
            }
        }, err => this.handleError(err))
    }

    public select(workflow: Workflow): void {
        this.selected = null;

        setTimeout(() => {
            this.selected = Workflow.createSubType(workflow);

            this.tabs = this.workflowService.TABS[this.target][this.selected.getDefaultSectionName()]
                .filter(tab => !tab.hide)
                .map( (tab, idx) => {
                    tab.index = idx;
                    return tab;
                });

            this.workflowService.loadSection(this.generateUriBuilderParams(this.selected), this.selected, this.selected.getDefaultSectionName())
                                .subscribe(() => {
                this.selectTab(true);
            }, err => this.handleError(err));
        });
    }

    public create(name: string): void {
        const newWorkflow = Workflow.createSubType({
            target: this.target,
            name: name
        });

        this.workflowService.create(this.generateUriBuilderParams(), newWorkflow, this.copyWorkflow ? { queryParams: {copy_from: this.copyWorkflow.id} } : null)
                            .subscribe(result => {
            this.showSimpleConfirmSnackbar(marker('workflow_management.message.create_success'));
            this.copyWorkflow = null;
            this.workflowList.addItem(result, true);
            this.select(result);
        }, err => this.handleError(err))
    }

    public copy(workflow: Workflow): void {
        const copyNameSuggestion = `${this.translateService.instant(marker('common.label.copy_of'))} ${workflow.name}`;
        this.copyWorkflow = workflow;
        this.createNameComponent.name.setValue(copyNameSuggestion);
        this.createNameComponent.panelOpen = true;
    }

    public delete(workflow: Workflow): void {
        this.workflowService.delete(this.generateUriBuilderParams(workflow))
                            .subscribe(result => {
            this.showSimpleConfirmSnackbar(marker('workflow_management.message.delete_success'));
            this.workflowList.removeItem(workflow);
        }, err => this.handleError(err))
    }

    public reorderWorkflows(): void {
        const opData: OpData[] = [];

        for (let i = 0, y = (this.workflowList.items.length); i < this.workflowList.items.length; i++, y--) {
            if (this.workflowList.items[i].sort_order !== y) {
                this.workflowList.items[i].sort_order = y;

                opData.push({
                    op: OP.replace,
                    path: `${this.workflowList.items[i].id}/sort_order`,
                    value: this.workflowList.items[i].sort_order
                });
            }
        }

        if (opData.length) {
            this.workflowService.patchWorkflowTarget(this.generateUriBuilderParams(), opData)
                                .subscribe(response => {
                    // No action needed
                }, err => { this.handleError(err)});
        }
    }

    public selectTab(reset?: boolean): void {
        if (reset) {
            this.tabGroup.selectedIndex = 0;
        }

        const newTab = this.tabs.find(tab => tab.index === this.tabGroup.selectedIndex);
        this.selectedTabContentCode = newTab && newTab.code;
    }

    private toggleActivateWorkflow(workflow: Workflow): void {
        const data = [{
            op: OP.replace,
            path: 'active',
            value: !workflow.active
        }];

        this.workflowService.patchAndUpdate(this.generateUriBuilderParams(workflow), workflow, data)
                            .subscribe(result => {
            // NO FURTHER ACTION NEEDED
        }, err => this.handleError(err));
    }

    private initActionListOptions(): void {
        this.actionListOptions = [{
                option: 'ACTIVATE',
                label: marker('user_management.btn.activate'),
                action: (item: Workflow) => {
                    this.toggleActivateWorkflow(item);
                },
                show: (item: Workflow) => {
                    return !item.active;
                },
                order: 1
            }, {
                option: 'DEACTIVATE',
                label: marker('user_management.btn.deactivate'),
                action: (item: Workflow) => {
                    this.toggleActivateWorkflow(item);
                },
                show: (item: Workflow) => {
                    return item.active;
                },
                order: 1
            },
            // Using the existing options of the action list for COPY and DELETE
            {
                option: ActionListOption.COPY,
                order: 2
            }, {
                option: ActionListOption.DELETE,
                order: 3
            }
        ];
    }

    protected handleError(error: HttpErrorResponse | Error | string): void {
        this.showSimpleErrorSnackbar();
        super.handleError(error);
    }

    protected generateUriBuilderParams(workflow?: Workflow): WorkflowUriBuilderParams {
        return new WorkflowUriBuilderParams(this.target, workflow);
    }

}
